import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Sparkles, Compass, Zap, Layers } from 'lucide-react';
import { FinancialTerminal } from '../components/FinancialTerminal';
import { LayerOperatingSystem } from '../components/LayerOperatingSystem';
import { IndustryShowcase } from '../components/IndustryShowcase';

export const Home: React.FC = () => {
  return (
    <div style={{ paddingTop: '80px' }}>
      {/* =========================================================================
          HERO SECTION
          ========================================================================= */}
      <section
        style={{
          position: 'relative',
          paddingTop: '60px',
          paddingBottom: '90px',
          overflow: 'hidden',
        }}
      >
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
              gap: '48px',
              alignItems: 'center',
            }}
          >
            {/* Left Content */}
            <div style={{ maxWidth: '620px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                <span className="badge-pill badge-red">
                  <Sparkles size={12} />
                  FINANCIAL INTELLIGENCE PLATFORM
                </span>
                <span className="badge-pill badge-neutral font-mono">
                  ४२ DEVANAGARI NUMERAL
                </span>
              </div>

              <h1
                style={{
                  fontSize: 'clamp(2.75rem, 5.5vw, 4.2rem)',
                  fontWeight: 800,
                  lineHeight: '1.08',
                  letterSpacing: '-0.03em',
                  color: 'var(--text-primary)',
                  marginBottom: '18px',
                }}
              >
                Finance.{' '}
                <br />
                Made{' '}
                <span
                  className="font-serif"
                  style={{
                    color: 'var(--brand-red)',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    textShadow: '0 0 30px var(--brand-red-glow)',
                  }}
                >
                  Intelligent.
                </span>
              </h1>

              <p
                style={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--brand-red-light)',
                  marginBottom: '16px',
                }}
              >
                MAKING ACCOUNTING EASY. TURNING NUMBERS INTO DECISIONS.
              </p>

              <p
                style={{
                  fontSize: '1.125rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.65',
                  marginBottom: '32px',
                }}
              >
                Most businesses run on numbers they don't fully understand or trust.{' '}
                <strong style={{ color: 'var(--text-primary)' }}>Forty2 brings structure to your accounting</strong>, turning financial data into clear, actionable decisions — with AI-driven insights and Chartered Accountant expertise behind it.
              </p>

              {/* CTAs */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '40px' }}>
                <Link to="/diagnostic" className="btn-primary" style={{ padding: '14px 28px', fontSize: '1rem' }}>
                  <span>Start Your Diagnostic</span>
                  <ArrowRight size={18} />
                </Link>

                <a href="#how-it-works" className="btn-secondary" style={{ padding: '14px 24px', fontSize: '1rem' }}>
                  <span>How it works</span>
                </a>
              </div>

              {/* Stat Pills */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '24px',
                  paddingTop: '24px',
                  borderTop: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className="font-mono" style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                    12
                  </span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.2' }}>
                    Point<br />Diagnostic
                  </span>
                </div>

                <div style={{ width: '1px', height: '36px', background: 'var(--border-subtle)' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className="font-mono" style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    3
                  </span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.2' }}>
                    Markets<br />Active
                  </span>
                </div>

                <div style={{ width: '1px', height: '36px', background: 'var(--border-subtle)' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                    ४२
                  </span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.2' }}>
                    The<br />Answer
                  </span>
                </div>
              </div>
            </div>

            {/* Right Interactive Terminal Widget */}
            <div>
              <FinancialTerminal />
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          INTERMISSION BANNER
          ========================================================================= */}
      <section
        style={{
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
          padding: '40px 0',
          textAlign: 'center',
        }}
      >
        <div className="container-max">
          <p
            style={{
              fontSize: 'clamp(1.25rem, 2.5vw, 1.85rem)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '-0.01em',
            }}
          >
            Most companies have financial data.{' '}
            <span style={{ color: 'var(--brand-red-light)' }}>Very few have financial intelligence.</span>
          </p>
        </div>
      </section>

      {/* =========================================================================
          THE PROBLEM SECTION
          ========================================================================= */}
      <section style={{ padding: '96px 0', position: 'relative' }}>
        <div className="container-max">
          <div style={{ maxWidth: '720px', marginBottom: '56px' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              THE PROBLEM
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 2.85rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: '1.18',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              Why Startups and MSMEs Struggle with Finance
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Traditional accounting tells you what happened. Financial intelligence tells you what to do next.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px',
            }}
          >
            {[
              {
                num: '01',
                title: 'Data Without Direction',
                text: 'You have P&L statements and balance sheets, but no clear path from numbers to decisions.',
                icon: Compass,
              },
              {
                num: '02',
                title: 'Reactive, Not Proactive',
                text: 'Finance teams spend time recording the past instead of planning the future.',
                icon: Zap,
              },
              {
                num: '03',
                title: 'Intelligence Gap',
                text: 'Large enterprises have financial intelligence built in. Startups and MSMEs are often left without that edge.',
                icon: TrendingUp,
              },
              {
                num: '04',
                title: 'System Fragmentation',
                text: 'Your data lives in Tally, Excel, WhatsApp, and email — not in one intelligent system.',
                icon: Layers,
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.num}
                  className="glass-card-interactive"
                  style={{
                    padding: '32px 28px',
                    backgroundColor: 'var(--bg-card)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <span className="font-mono" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                      {card.num}
                    </span>
                    <Icon size={20} color="var(--text-muted)" />
                  </div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>
                    {card.title}
                  </h3>
                  <p style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {card.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================================================================
          TRANSITION BANNER
          ========================================================================= */}
      <section
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: '40px 0',
          textAlign: 'center',
        }}
      >
        <div className="container-max">
          <p
            style={{
              fontSize: 'clamp(1.2rem, 2.2vw, 1.75rem)',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Finance should not just record the past.{' '}
            <span style={{ color: 'var(--brand-red)' }}>It should help decide the future.</span>
          </p>
        </div>
      </section>

      {/* =========================================================================
          THE SOLUTION / HOW FORTY2 WORKS
          ========================================================================= */}
      <section id="how-it-works" style={{ padding: '96px 0', position: 'relative' }}>
        <div className="container-max">
          <div style={{ maxWidth: '750px', marginBottom: '20px' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              THE SOLUTION
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 2.85rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: '1.18',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              How Forty2 Works
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              We transform your existing accounting into an intelligent operating system — industry and technology agnostic.
            </p>
          </div>

          {/* 4-Layer Operating System Component */}
          <LayerOperatingSystem />
        </div>
      </section>

      {/* =========================================================================
          INDUSTRY SOLUTIONS RADAR (LIVE SHOWCASE)
          ========================================================================= */}
      <section style={{ padding: '96px 0', borderTop: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-deep)' }}>
        <div className="container-max">
          <div style={{ maxWidth: '780px', marginBottom: '44px' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              OPERATIONAL BLUEPRINTS
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 2.85rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: '1.18',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              Intelligence Tailored to Your Specific Industry
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              Whether it is BPCL petrol pump IoT telemetry, 3-way Swiggy/Zomato reconciliation, or MSME Section 43B(h) compliance, Forty2 adapts to the real operational rhythms of your business.
            </p>
          </div>

          <IndustryShowcase />
        </div>
      </section>

      {/* =========================================================================
          QUOTE SECTION
          ========================================================================= */}
      <section
        style={{
          padding: '80px 0',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--bg-surface)',
          textAlign: 'center',
        }}
      >
        <div className="container-max" style={{ maxWidth: '850px' }}>
          <div style={{ fontSize: '3rem', color: 'var(--brand-red)', lineHeight: 1, marginBottom: '8px' }}>“</div>
          <blockquote
            className="font-serif"
            style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)',
              fontWeight: 400,
              fontStyle: 'italic',
              color: 'var(--text-primary)',
              lineHeight: '1.3',
              marginBottom: '24px',
            }}
          >
            Businesses don't need more data. They need more clarity.
          </blockquote>
          <span style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--brand-red-light)', fontWeight: 600 }}>
            FORTY2 OPERATING PRINCIPLE
          </span>
        </div>
      </section>

      {/* =========================================================================
          DIAGNOSTIC CTA CALLOUT
          ========================================================================= */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div
            className="glass-panel-elevated"
            style={{
              padding: '60px 40px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid var(--border-red)',
            }}
          >
            <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
              INTERACTIVE BENCHMARK
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                marginBottom: '16px',
                letterSpacing: '-0.02em',
              }}
            >
              Where Does Your Business Stand?
            </h2>
            <p
              style={{
                fontSize: '1.1rem',
                color: 'var(--text-secondary)',
                maxWidth: '640px',
                margin: '0 auto 32px auto',
                lineHeight: '1.6',
              }}
            >
              Take our 2-minute 12-point diagnostic checklist to identify critical gaps in your cash runway, margin control, and reporting agility.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <Link to="/diagnostic" className="btn-primary" style={{ padding: '16px 36px', fontSize: '1.05rem' }}>
                <span>Launch 12-Point Diagnostic</span>
                <ArrowRight size={18} />
              </Link>
              <Link to="/what-we-do" className="btn-secondary" style={{ padding: '16px 28px', fontSize: '1.05rem' }}>
                <span>Explore What We Do</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
